import { FC, useMemo } from "react";
import cx from "classnames";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import styles from "./KeywordsDataSourcesDropdown.module.scss";
import { Select } from "src/components";
import { getIconByName, isKeywordsDataSourceTypeGuard } from "src/utils";
import { selectSubscriptionPlanKeywordsDataSources } from "src/store/selectors";
import {
  SEARCH_KEYWORDS_DATA_SOURCE_MAP,
  SEARCH_KEYWORDS_DEFAULT_DATA_SOURCE_ICON,
} from "src/constants";
import type {
  SelectProps,
  SelectOption,
} from "src/components/selectors/Select/types";

type Props = Pick<SelectProps, "openingDirection"> & {
  dataSource: Nullable<Search.KeywordsDataSource>;
  isDisabled?: boolean;
  setDataSource: (value: Search.KeywordsDataSource) => void;
  hasDefaultStyles?: boolean;
  error?: string;
};

export const KeywordsDataSourcesDropdown: FC<Props> = ({
  error,
  dataSource,
  setDataSource,
  isDisabled = false,
  hasDefaultStyles = false,
  openingDirection = "bottom-end",
}) => {
  const { t } = useTranslation();

  const subscriptionPlanKeywordsDataSources = useSelector(
    selectSubscriptionPlanKeywordsDataSources,
  );

  const keywordsDataSourceOptions = useMemo<
    SelectOption<Search.KeywordsDataSource>[]
  >(
    () =>
      subscriptionPlanKeywordsDataSources.map((keywordsDataSource) => ({
        value: keywordsDataSource,
        label: t(SEARCH_KEYWORDS_DATA_SOURCE_MAP[keywordsDataSource].labelKey),
        renderIcon: () =>
          getIconByName(
            SEARCH_KEYWORDS_DATA_SOURCE_MAP[keywordsDataSource].icon,
          ),
      })),
    [subscriptionPlanKeywordsDataSources, t],
  );

  const icon = useMemo<AppIcon>(() => {
    if (!dataSource) return SEARCH_KEYWORDS_DEFAULT_DATA_SOURCE_ICON;

    return SEARCH_KEYWORDS_DATA_SOURCE_MAP[dataSource].icon;
  }, [dataSource]);

  const onDataSourceSelect = (value: string): void => {
    if (isKeywordsDataSourceTypeGuard(value)) setDataSource(value);
  };

  return (
    <div className={hasDefaultStyles ? "" : styles.wrapper}>
      <Select
        icon={icon}
        value={dataSource || ""}
        hasFilter={false}
        isDisabled={isDisabled}
        columnsCount={2}
        changeHandler={onDataSourceSelect}
        options={keywordsDataSourceOptions}
        openingDirection={openingDirection}
        placeholder={t("component.keywords_data_sources.placeholder")}
        title={t("component.keywords_data_sources.title")}
        inputClassName={hasDefaultStyles ? "" : styles.input}
        selectClassName={cx(
          hasDefaultStyles ? "" : styles.select,
          error ? styles.selectError : "",
        )}
        dropdownClassName={hasDefaultStyles ? "" : styles.dropdown}
      />
      {Boolean(error) && (
        <div className={styles.error}>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};
